import { useState, useEffect } from 'react';
import { SystemParametersService } from '../services/systemParameters';

export interface SystemParameter {
  id: string;
  key: string;
  value: string;
  description?: string;
  category?: string;
  dataType: string;
  isActive: boolean;
  isEncrypted: boolean;
  createdBy?: string;
  updatedBy?: string;
  createdAt: string;
  updatedAt: string;
}

export const useSystemParameters = () => {
  const [parameters, setParameters] = useState<SystemParameter[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchParameters = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await SystemParametersService.getAllParameters();
      setParameters(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch parameters');
    } finally {
      setLoading(false);
    }
  };

  const getParameter = (key: string): SystemParameter | null => {
    return parameters.find(p => p.key === key) || null;
  };

  const getStringValue = (key: string, defaultValue?: string): string | null => {
    const param = getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    return param.value;
  };

  const getNumberValue = (key: string, defaultValue?: number): number | null => {
    const param = getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    const numValue = parseFloat(param.value);
    return isNaN(numValue) ? (defaultValue || null) : numValue;
  };

  const getBooleanValue = (key: string, defaultValue?: boolean): boolean | null => {
    const param = getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    return param.value.toLowerCase() === 'true';
  };

  const getJsonValue = <T = any>(key: string, defaultValue?: T): T | null => {
    const param = getParameter(key);
    if (!param || !param.isActive) {
      return defaultValue || null;
    }
    try {
      return JSON.parse(param.value);
    } catch {
      return defaultValue || null;
    }
  };

  useEffect(() => {
    fetchParameters();
  }, []);

  return {
    parameters,
    loading,
    error,
    fetchParameters,
    getParameter,
    getStringValue,
    getNumberValue,
    getBooleanValue,
    getJsonValue,
  };
};

